import { NOT_AVAILABLE } from '@constants/common';
import { withTooltip } from '@hooks/common/useDynamicColumns/utils/withTooltip';
import { get } from 'lodash-es';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import EmailIcon from '@mui/icons-material/Email';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SourceIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PhoneIcon from '@mui/icons-material/Phone';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Box, Button, Chip, Rating, Tooltip, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

// Helper to check if data should be blurred (not preview row and not purchased)
const shouldBlurData = (row: LocalSearchLeadEntity): boolean => {
  const isPreviewRow = row._isPreviewRow === true;
  const isPurchased = row.purchased === true || row.is_purchased === true;
  return !isPreviewRow && !isPurchased;
};

// Helper to render blurred text
const renderBlurredText = (text: string, blurred: boolean) => (
  <Typography
    variant="body2"
    noWrap
    sx={{
      filter: blurred ? 'blur(5px)' : 'none',
      userSelect: blurred ? 'none' : 'auto',
      transition: 'filter 0.2s ease',
    }}
  >
    {text}
  </Typography>
);

interface LocalSearchLeadEntity {
  // Flattened fields from API
  leads_id?: string;
  name?: string;
  phone?: string;
  email?: string | string[];
  address?: string;
  rating?: number | string;
  categories?: string | string[];
  source?: string;
  scraper_name?: string;
  city?: string;
  state?: string;
  website?: string;
  company?: string;
  // Purchase status
  purchased?: boolean;
  is_purchased?: boolean;
  // Row index and preview status (added by ScrapekingDbDynamicDataGrid)
  _rowIndex?: number;
  _isPreviewRow?: boolean;
  _onUnlockLead?: (leadId: string) => Promise<{ success: boolean; error?: string }>;
  // Additional contacts data
  has_additional_contacts?: boolean;
  additional_contacts_count?: number;
  additionalData?: {
    additional_contacts?: any[];
    additional_emails?: string[];
    additional_phones?: string[];
    additional_contacts_unlocked?: boolean;
  };
  additional_data?: {
    additional_contacts?: any[];
    additional_emails?: string[];
    additional_phones?: string[];
    additional_contacts_unlocked?: boolean;
  };
  // Timestamps
  created_at?: string;
  updated_at?: string;
  // Nested data from API
  data?: {
    name?: string;
    phone?: string;
    email?: string | string[];
    address?: string;
    rating?: number | string;
    categories?: string | string[];
    source?: string;
    city?: string;
    state?: string;
    website?: string;
    company?: string;
    enriched_at?: string;
    // Additional contacts directly in data (from API)
    additional_contacts?: Array<{
      type?: string;
      value?: string;
      name?: string;
      email?: string;
      phone?: string;
      title?: string;
    }>;
    additional_emails?: string[];
    additional_phones?: string[];
    additional_contacts_unlocked?: boolean;
    additionalData?: {
      additional_contacts?: any[];
      additional_emails?: string[];
      additional_phones?: string[];
      additional_contacts_unlocked?: boolean;
    };
  };
  raw?: Record<string, any>;
  [key: string]: any;
}

// Helper to get value from multiple possible paths
const getValue = (row: LocalSearchLeadEntity, field: string) =>
  // Try flattened field first (from API), then nested data, then raw
  row[field as keyof LocalSearchLeadEntity] || get(row, `data.${field}`) || get(row, `raw.${field}`) || '';
export const createLocalSearchColumns = () => [
  {
    field: 'unlock_lead',
    headerName: '',
    headerClassName: 'super-bold--header',
    width: 110,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: LocalSearchLeadEntity }) => {
      const isPurchased = row.purchased === true || row.is_purchased === true;
      const isPreviewRow = row._isPreviewRow === true;

      // Count additional contacts for display
      const additionalData = row.additionalData || row.additional_data || row.data?.additionalData || {};
      const additionalContacts = additionalData.additional_contacts || row.data?.additional_contacts || [];
      const additionalEmails = additionalData.additional_emails || row.data?.additional_emails || [];
      const additionalPhones = additionalData.additional_phones || row.data?.additional_phones || [];

      const extraContactsCount =
        row.additional_contacts_count ||
        additionalContacts.length ||
        Math.max(additionalEmails.length, additionalPhones.length);

      // Lead unlock cost is 1 credit
      const leadCost = 1;

      // If already purchased, show unlocked status
      if (isPurchased) {
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <LockOpenIcon sx={{ color: '#00d4aa', fontSize: 16 }} />
            <Typography
              variant="caption"
              sx={{
                color: '#00d4aa',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            >
              Unlocked
            </Typography>
          </Box>
        );
      }

      // For preview rows, show that it's a free preview
      if (isPreviewRow) {
        return (
          <Tooltip title="This is a free preview row - purchase to save to your leads">
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography
                variant="caption"
                sx={{
                  color: '#90caf9',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                }}
              >
                Preview
              </Typography>
            </Box>
          </Tooltip>
        );
      }

      // For locked rows, show unlock button with price
      return (
        <Tooltip
          title={
            extraContactsCount > 0
              ? `Unlock lead (${leadCost} credit) + ${extraContactsCount} extra contact${extraContactsCount > 1 ? 's' : ''}`
              : `Purchase lead for ${leadCost} credit to reveal contact info`
          }
        >
          <Button
            size="small"
            variant="contained"
            onClick={e => {
              e.stopPropagation();
            }}
            sx={{
              backgroundColor: 'rgba(0, 212, 170, 0.15)',
              color: '#00d4aa',
              fontSize: '0.65rem',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 212, 170, 0.25)',
              },
            }}
            startIcon={<ShoppingCartIcon sx={{ fontSize: '12px !important' }} />}
          >
            {leadCost} credit
            {extraContactsCount > 0 && (
              <Typography
                component="span"
                sx={{
                  ml: 0.5,
                  fontSize: '0.6rem',
                  color: '#ffb74d',
                }}
              >
                +{extraContactsCount}
              </Typography>
            )}
          </Button>
        </Tooltip>
      );
    },
  },
  {
    field: 'name',
    headerName: 'Business Name',
    headerClassName: 'super-bold--header',
    width: 250,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: LocalSearchLeadEntity }) => {
      const name = getValue(row, 'name');
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <BusinessIcon sx={{ color: '#00d4aa', fontSize: 18, flexShrink: 0 }} />
          <Typography variant="body2" fontWeight={500} noWrap>
            {name || NOT_AVAILABLE}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: 'phone',
    headerName: 'Phone',
    headerClassName: 'super-bold--header',
    width: 160,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: LocalSearchLeadEntity }) => {
      const phone = getValue(row, 'phone');
      const blurred = shouldBlurData(row);
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <PhoneIcon sx={{ color: blurred ? '#666' : '#4fc3f7', fontSize: 16, flexShrink: 0 }} />
          {blurred ? (
            <Tooltip title="Purchase this lead to reveal phone number">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {renderBlurredText(phone || '(555) 123-4567', true)}
              </Box>
            </Tooltip>
          ) : (
            <Typography variant="body2" noWrap>
              {phone || NOT_AVAILABLE}
            </Typography>
          )}
        </Box>
      );
    },
  },
  {
    field: 'email',
    headerName: 'Email',
    headerClassName: 'super-bold--header',
    width: 220,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: LocalSearchLeadEntity }) => {
      let email = getValue(row, 'email');
      // Handle array of emails
      if (Array.isArray(email)) {
        email = email[0] || '';
      }
      const blurred = shouldBlurData(row);

      // Check if email is a placeholder (no real email yet)
      const isPlaceholder = !email || email.startsWith('noemail_') || email.startsWith('biz-');
      const enrichedAt = get(row, 'data.enriched_at') || get(row, 'enriched_at');
      const hasWebsite = getValue(row, 'website');
      const hasName = getValue(row, 'name');

      // Determine enrichment state
      const isEnriching = isPlaceholder && !enrichedAt && (hasWebsite || hasName);
      if (isEnriching && !blurred) {
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={14} sx={{ color: '#ba68c8', flexShrink: 0 }} />
            <Typography variant="body2" noWrap sx={{ color: '#999', fontStyle: 'italic', fontSize: '0.8rem' }}>
              Finding email...
            </Typography>
          </Box>
        );
      }

      // Show clean display for leads where enrichment found nothing
      const displayEmail = isPlaceholder ? '' : email;

      return (
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon
            sx={{ color: blurred ? '#666' : displayEmail ? '#ba68c8' : '#555', fontSize: 16, flexShrink: 0 }}
          />
          {blurred ? (
            <Tooltip title="Purchase this lead to reveal email address">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {renderBlurredText(email || 'contact@example.com', true)}
              </Box>
            </Tooltip>
          ) : (
            <Typography variant="body2" noWrap sx={{ color: displayEmail ? 'inherit' : '#666' }}>
              {displayEmail || NOT_AVAILABLE}
            </Typography>
          )}
        </Box>
      );
    },
  },
  {
    field: 'address',
    headerName: 'Address',
    headerClassName: 'super-bold--header',
    flex: 1,
    minWidth: 280,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: LocalSearchLeadEntity }) => {
      const address = getValue(row, 'address');
      const city = getValue(row, 'city');
      const state = getValue(row, 'state');
      const blurred = shouldBlurData(row);

      let fullAddress = address;
      if (!fullAddress && (city || state)) {
        fullAddress = [city, state].filter(Boolean).join(', ');
      }

      return (
        <Box display="flex" alignItems="center" gap={1}>
          <LocationOnIcon sx={{ color: blurred ? '#666' : '#ef5350', fontSize: 16, flexShrink: 0 }} />
          {blurred ? (
            <Tooltip title="Purchase this lead to reveal full address">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {renderBlurredText(fullAddress || '123 Main St, City, ST', true)}
              </Box>
            </Tooltip>
          ) : (
            withTooltip(fullAddress || NOT_AVAILABLE)
          )}
        </Box>
      );
    },
  },
  {
    field: 'rating',
    headerName: 'Rating',
    headerClassName: 'super-bold--header',
    width: 140,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: LocalSearchLeadEntity }) => {
      const rating = getValue(row, 'rating');
      const numRating = typeof rating === 'string' ? parseFloat(rating) : rating;

      if (!numRating || Number.isNaN(numRating)) {
        return <Typography variant="body2">{NOT_AVAILABLE}</Typography>;
      }

      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Rating value={numRating} precision={0.5} size="small" readOnly />
          <Typography variant="body2" color="text.secondary">
            ({numRating.toFixed(1)})
          </Typography>
        </Box>
      );
    },
  },
  {
    field: 'categories',
    headerName: 'Categories',
    headerClassName: 'super-bold--header',
    width: 200,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: LocalSearchLeadEntity }) => {
      const categories = getValue(row, 'categories');

      if (!categories || (Array.isArray(categories) && categories.length === 0)) {
        return <Typography variant="body2">{NOT_AVAILABLE}</Typography>;
      }

      const categoryList = Array.isArray(categories) ? categories : [categories];
      const displayCategory = categoryList[0];

      return (
        <Box display="flex" alignItems="center" gap={1}>
          <CategoryIcon sx={{ color: '#ffb74d', fontSize: 16, flexShrink: 0 }} />
          <Chip
            label={displayCategory}
            size="small"
            sx={{
              backgroundColor: 'rgba(0, 212, 170, 0.15)',
              color: '#00d4aa',
              fontSize: '0.75rem',
              height: 24,
            }}
          />
          {categoryList.length > 1 && (
            <Typography variant="caption" color="text.secondary">
              +{categoryList.length - 1}
            </Typography>
          )}
        </Box>
      );
    },
  },
  {
    field: 'source',
    headerName: 'Source',
    headerClassName: 'super-bold--header',
    width: 140,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: LocalSearchLeadEntity }) => {
      const source = getValue(row, 'source') || getValue(row, 'scraper_name');

      return (
        <Box display="flex" alignItems="center" gap={1}>
          <SourceIcon sx={{ color: '#90caf9', fontSize: 16, flexShrink: 0 }} />
          <Chip
            label={source || 'Unknown'}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              height: 22,
              borderColor: 'rgba(255,255,255,0.2)',
            }}
          />
        </Box>
      );
    },
  },
  {
    field: 'created_at',
    headerName: 'Date Added',
    headerClassName: 'super-bold--header',
    width: 155,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: LocalSearchLeadEntity }) => {
      const dateStr = row.created_at || get(row, 'data.scraped_at') || get(row, 'data.created_at');
      if (!dateStr) {
        return (
          <Typography variant="body2" color="text.secondary">
            {NOT_AVAILABLE}
          </Typography>
        );
      }
      try {
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) {
          return (
            <Typography variant="body2" color="text.secondary">
              {NOT_AVAILABLE}
            </Typography>
          );
        }
        const month = date.toLocaleString('en-US', { month: 'short' });
        const day = date.getDate();
        const year = date.getFullYear();
        const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        const fullDate = `${month} ${day}, ${year} ${time}`;
        const shortDate = `${month} ${day} ${time}`;
        return (
          <Tooltip title={fullDate}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <AccessTimeIcon sx={{ color: '#78909c', fontSize: 14, flexShrink: 0 }} />
              <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', color: '#aaa' }}>
                {shortDate}
              </Typography>
            </Box>
          </Tooltip>
        );
      } catch {
        return (
          <Typography variant="body2" color="text.secondary">
            {NOT_AVAILABLE}
          </Typography>
        );
      }
    },
  },
  {
    field: 'extra_contacts',
    headerName: 'Extra Contacts',
    headerClassName: 'super-bold--header',
    width: 130,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: LocalSearchLeadEntity }) => {
      // Check for additional contacts from various data sources
      // Check both wrapped additionalData and direct data.additional_contacts from API
      const additionalData = row.additionalData || row.additional_data || row.data?.additionalData || {};
      const additionalContacts = additionalData.additional_contacts || row.data?.additional_contacts || [];
      const additionalEmails = additionalData.additional_emails || row.data?.additional_emails || [];
      const additionalPhones = additionalData.additional_phones || row.data?.additional_phones || [];
      const isUnlocked = additionalData.additional_contacts_unlocked || row.data?.additional_contacts_unlocked || false;

      // Also check for has_additional_contacts flag from API
      const hasExtraContacts =
        row.has_additional_contacts ||
        additionalContacts.length > 0 ||
        additionalEmails.length > 0 ||
        additionalPhones.length > 0;

      const count =
        row.additional_contacts_count ||
        additionalContacts.length ||
        Math.max(additionalEmails.length, additionalPhones.length);

      if (!hasExtraContacts) {
        return (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        );
      }

      // Calculate total cost for unlocking
      const costPerContact = 0.25;
      const totalCost = (count * costPerContact).toFixed(2);

      return (
        <Tooltip
          title={
            isUnlocked
              ? `${count} additional contact${count > 1 ? 's' : ''} - Unlocked`
              : `${count} contact${count > 1 ? 's' : ''} available • Unlock for ${totalCost} credits`
          }
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            {isUnlocked ? (
              <GroupAddIcon sx={{ color: '#00d4aa', fontSize: 18 }} />
            ) : (
              <LockIcon sx={{ color: '#ffb74d', fontSize: 16 }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color: isUnlocked ? '#00d4aa' : '#ffb74d',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            >
              {count}
            </Typography>
          </Box>
        </Tooltip>
      );
    },
  },
];
